import slugg from "slugg";

import MetabaseSettings from "metabase/utils/settings";
import type { QuestionCreatorOpts } from "metabase-lib/v1/Question";
import Question from "metabase-lib/v1/Question";
import type {
  CardId,
  ConcreteTableId,
  Card as SavedCard,
  UnsavedCard,
} from "metabase-types/api";
import type { EntityToken } from "metabase-types/api/entity";

import { type CardUrlBuilderParams, card as urlForCard } from "./cards";
import { appendSlug } from "./utils";

type QuestionUrlBuilderOpts = {
  originalQuestion?: Question;
  query?: Record<string, any>;
  creationType?: string;
};

export function question(
  question: Question,
  { originalQuestion, query, creationType }: QuestionUrlBuilderOpts = {},
) {
  const isDirty =
    originalQuestion != null && question.isDirtyComparedTo(originalQuestion);

  return urlForCard(question.cardWithNormalizedQuery(), {
    query,
    creationType,
    parameterValues: question._parameterValues,
    includeDisplayIsLocked: true,
    // dirty questions should always render as unsaved
    forceUnsaved: isDirty,
  });
}

export function serializedQuestion(
  card: SavedCard | UnsavedCard,
  opts: Omit<CardUrlBuilderParams, "forceUnsaved"> = {},
) {
  return urlForCard(card, { ...opts, forceUnsaved: true });
}

type NewQuestionUrlBuilderParams = QuestionCreatorOpts & {
  mode?: "view" | "notebook" | "query" | "ask";
  creationType?: string;
  objectId?: number | string;
};

export function newQuestion({
  mode,
  creationType,
  objectId,
  ...options
}: NewQuestionUrlBuilderParams) {
  if (mode === "ask") {
    return `/question/ask`;
  }

  const q = Question.create(options);
  const url = question(q, {
    creationType,
    query: objectId === undefined ? {} : { objectId },
  });
  const type = q.type();

  if (mode) {
    const pathType = type === "metric" ? "question" : type;
    return url.replace(/^\/(question|model|metric)/, `/${pathType}\/${mode}`);
  }

  return url;
}

export function publicQuestion({
  uuid,
  type = null,
  query,
  includeSiteUrl = true,
}: {
  uuid: string;
  type?: string | null;
  query?: string;
  includeSiteUrl?: boolean;
}) {
  const siteUrl = includeSiteUrl ? MetabaseSettings.get("site-url") : "";
  const searchQuery = query ? `?${query}` : "";
  return (
    `${siteUrl}/public/question/${uuid}` +
    (type ? `.${type}` : "") +
    searchQuery
  );
}

export function embedCard(token: EntityToken, type: string | null = null) {
  return `/embed/question/${token}` + (type ? `.${type}` : ``);
}

export function tableRowsQuery(
  databaseId: number | string,
  tableId: number | string,
  metricId?: number | string,
  segmentId?: number | string,
) {
  let query = `?db=${databaseId}&table=${tableId}`;

  if (metricId) {
    query += `&metric=${metricId}`;
  }

  if (segmentId) {
    query += `&segment=${segmentId}`;
  }

  // QB parses this querystring-in-hash to build an ad-hoc question — confusing, but load-bearing.
  return `/question#${query}`;
}

/**
 * Canonical, human-readable URL for viewing a table as an ad-hoc question,
 * e.g. `/table/2-orders`. This is the entry URL only: the QB falls back to the
 * `/question#<hash>` form (see {@link question}) as soon as the question is
 * edited away from the table's pristine default.
 *
 * Only concrete (numeric) table ids are supported — the `/table/:slug` route
 * parses ids via {@link extractEntityId}. Callers holding a possibly-virtual id
 * (e.g. `card__17` from the Saved Questions virtual database) must fall back to
 * {@link tableRowsQuery} or the ad-hoc {@link question} URL.
 */
export function table({
  id,
  name,
}: {
  id: ConcreteTableId;
  name?: string | null;
}) {
  return appendSlug(`/table/${id}`, name ? slugg(name) : undefined);
}

export function xrayModel(id: CardId) {
  return `/auto/dashboard/model/${id}`;
}
